import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task, restartableTask, timeout } from 'ember-concurrency';
import type LocationModel from 'garaje/models/location';
import type PluginModel from 'garaje/models/plugin';
import type PluginInstallModel from 'garaje/models/plugin-install';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type DiplomatPluginsService from 'garaje/services/diplomat-plugins';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import type { DiplomatAblePluginOption } from 'garaje/utils/binary-download-options';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import { wrappedFetch } from 'garaje/utils/wrapped-fetch';
import zft from 'garaje/utils/zero-for-tests';

const DIPLOMAT_CLIENTS_REFRESH_INTERVAL = zft(60000); // 60 seconds
const MANUAL_REFRESH_COOLDOWN = zft(5000); // 5 seconds minimum between manual refreshes

interface WorkplaceSettingsBinaryDownloadDiplomatConfigdModalArgs {
  location: LocationModel;
  plugins: PluginModel[];
  pluginInstalls: PluginInstallModel[];
  diplomatClients: DiplomatClient[];
  onUpdateDiplomatClient: () => Promise<void>;
}

interface DiplomatClientTableRow {
  id: string;
  secret: string;
  enabled: boolean;
  connectionStatus: string;
  pluginName: string;
  pluginId: string;
  pluginInstallId: number;
  internalUrl: string;
}

interface DiplomatClient {
  id: string;
  secret: string;
  location_id: number;
  enabled: boolean;
  plugin_install_id: number;
  plugin_id: string;
  integration_name: string;
  internal_url: string;
  connected: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * @param {Class<Location>} location Location
 * @param {Boolean} [disabled] If true, open button is disabled
 */
export default class WorkplaceSettingsBinaryDownloadDiplomatConfigdModal extends Component<WorkplaceSettingsBinaryDownloadDiplomatConfigdModalArgs> {
  @service declare workplaceMetrics: WorkplaceMetricsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare store: Store;
  @service declare state: StateService;
  @service declare ajax: AjaxFetchService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare diplomatPlugins: DiplomatPluginsService;

  @tracked isSettingsPanelOpen = false;
  @tracked diplomatEnabledPluginsOptions: DiplomatAblePluginOption[] = [];
  @tracked showEditDiplomatClient = false;
  @tracked showPairClient = false;
  @tracked showCreateDiplomatClient = false;
  @tracked selectedClient: DiplomatClientTableRow | null = null;
  @tracked clientToDelete: DiplomatClient | null = null;
  @tracked isInCooldown = false;

  get isPairingFlowEnabled(): boolean {
    return this.featureFlags.isEnabled('diplomat-client-pairing-flow');
  }

  refreshTask = restartableTask(async () => {
    await this.args.onUpdateDiplomatClient();
  });

  autoRefreshTask = restartableTask(async () => {
    while (true) {
      await timeout(DIPLOMAT_CLIENTS_REFRESH_INTERVAL);

      // Only refresh if the page is visible, diplomat table is visible, and not in manual cooldown
      if (!document.hidden && this.isSettingsPanelOpen && !this.isInCooldown) {
        await this.refreshTask.perform();
      }
    }
  });

  /**
   * Check if the refresh button should be disabled
   */
  get isRefreshDisabled(): boolean {
    return this.refreshTask.isRunning || this.isInCooldown;
  }

  /**
   * Shows settings panel
   */
  @action
  showSettings(): void {
    this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_BINARY_DOWNLOAD_DIPLOMAT_CONFIG_EDIT_BUTTON_CLICKED');
    this.isSettingsPanelOpen = true;
    void this.autoRefreshTask.perform();
  }

  /**
   * Hides settings panel
   */
  @action
  hideSettings(): void {
    this.isSettingsPanelOpen = false;
    void this.autoRefreshTask.cancelAll();
  }

  @action
  editClient(client: DiplomatClientTableRow): void {
    this.setSelectedClient(client);
    this.showEditDiplomatClient = true;
  }

  @action
  setSelectedClient(client: DiplomatClientTableRow): void {
    this.selectedClient = client;
  }

  @action
  showPairClientModal(): void {
    this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_BINARY_DOWNLOAD_DIPLOMAT_CONFIG_PAIR_CLIENT_BUTTON_CLICKED');
    this.showPairClient = true;
  }

  @action
  showCreateClientModal(): void {
    this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_BINARY_DOWNLOAD_DIPLOMAT_CONFIG_CREATE_CLIENT_BUTTON_CLICKED');
    this.showCreateDiplomatClient = true;
  }

  @action
  async onPairClientUpdate(): Promise<void> {
    // Immediate refresh when client is paired
    await this.args.onUpdateDiplomatClient();

    // Queue another refresh 5 seconds later to ensure connection status is updated
    await timeout(5000);
    await this.refreshTask.perform();
  }

  @action
  async manualRefresh(): Promise<void> {
    if (this.isRefreshDisabled) {
      return;
    }

    try {
      await this.refreshTask.perform();
    } finally {
      // Restart auto-refresh timer after manual refresh
      void this.autoRefreshTask.perform();

      // Keep button disabled for cooldown after refresh completes
      this.isInCooldown = true;

      // Add minimum 5-second delay before allowing another manual refresh
      await timeout(MANUAL_REFRESH_COOLDOWN);
      this.isInCooldown = false;
    }
  }

  willDestroy(): void {
    super.willDestroy();
    void this.autoRefreshTask.cancelAll();
    void this.refreshTask.cancelAll();
  }

  get formattedDiplomatClients(): object[] {
    return this.args.diplomatClients.map((diplomatClient) => {
      const formattedDiplomatClient = { ...diplomatClient };
      // Use the integration_name from the API response instead of calculating it
      (formattedDiplomatClient as Record<string, unknown>)['plugin_name'] = diplomatClient.integration_name;
      return formattedDiplomatClient;
    });
  }

  deleteClientTask = task(async () => {
    if (!this.clientToDelete) {
      return;
    }
    const host = this.diplomatPlugins.getDiplomatServerHost();
    const url: string = urlBuilder.diplomatServer.deleteDiplomatClientUrl(this.clientToDelete.id, host);
    try {
      await wrappedFetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/vnd.api+json',
        },
        credentials: 'include',
      });
      this.clientToDelete = null;
      await this.args.onUpdateDiplomatClient();
      this.flashMessages.showAndHideFlash('success', 'Successfully deleted.');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });
}
